'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { QuickAddModal } from './Dashboard/QuickAddModal';
import { SelectBankrollModal } from './Bankrolls/SelectBankrollModal';
import { AddTransactionModal } from './Bankrolls/AddTransactionModal';
import { AddBankrollModal } from './Bankrolls/AddBankrollModal';
import { AddExpenseModal } from './Expenses/AddExpenseModal';
import { NewSessionStepper } from './Sessions/NewSessionStepper';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';
import type { ParsedReceipt } from '@/lib/parse-receipt-text';
import type { ReceiptScanButtonHandle } from './Expenses/ReceiptScanButton';
import { SubscriptionRequiredDialog } from './SubscriptionRequiredDialog';
import { openStripeResubscribeFlow } from '@/lib/open-stripe-resubscribe';

const ReceiptScanButton = dynamic(
  () => import('./Expenses/ReceiptScanButton').then((m) => ({ default: m.ReceiptScanButton })),
  { ssr: false }
);

export const GlobalQuickAdd: React.FC = () => {
  const router = useRouter();
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [isSelectBankrollOpen, setIsSelectBankrollOpen] = useState(false);
  const [isAddTransactionOpen, setIsAddTransactionOpen] = useState(false);
  const [isAddBankrollOpen, setIsAddBankrollOpen] = useState(false);
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [isScanReceiptOpen, setIsScanReceiptOpen] = useState(false);
  const [isScanningReceipt, setIsScanningReceipt] = useState(false);
  const [scannedReceiptData, setScannedReceiptData] = useState<ParsedReceipt | null>(null);
  const [isNewSessionOpen, setIsNewSessionOpen] = useState(false);
  const [newSessionMode, setNewSessionMode] = useState<'online' | 'live' | null>(null);
  const [newSessionEditId, setNewSessionEditId] = useState<string | null>(null);
  const [newSessionReturnTo, setNewSessionReturnTo] = useState<string | null>(null);
  const [newSessionPreSelectedBankrollId, setNewSessionPreSelectedBankrollId] = useState<string | null>(null);
  const [selectedBankrollId, setSelectedBankrollId] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('EUR');
  const scanTriggerRef = useRef<ReceiptScanButtonHandle | null>(null);
  const addBankrollOpenedFromSessionRef = useRef(false);
  const subscriptionCheckRunningRef = useRef(false);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);

  const buildSubscribePath = useCallback(() => {
    if (typeof window === 'undefined') return '/fr/subscribe';
    const locale = window.location.pathname.split('/')[1] || 'fr';
    return `/${locale}/subscribe`;
  }, []);

  const ensureCanCreateAction = useCallback(async () => {
    if (subscriptionCheckRunningRef.current) return false;
    subscriptionCheckRunningRef.current = true;
    try {
      const response = await fetch('/api/subscription/check');
      if (!response.ok) return true;
      const data = await response.json();
      if (data?.hasActiveSubscription) return true;
      setIsSubscriptionDialogOpen(true);
      return false;
    } catch {
      return true;
    } finally {
      subscriptionCheckRunningRef.current = false;
    }
  }, []);

  // Raccourci clavier Cmd+K (Mac) ou Ctrl+K (Windows/Linux) - Global
  useKeyboardShortcut('k', () => {
    setIsQuickAddOpen(true);
  }, {
    metaKey: true, // Accepter Cmd (Mac) ou Ctrl (Windows/Linux)
  });

  // Écouter les événements personnalisés pour ouvrir le modal depuis n'importe où
  useEffect(() => {
    type OpenNewSessionDetail = {
      editId?: string;
      returnTo?: string;
      preSelectedBankrollId?: string;
    };
    type OpenAddBankrollDetail = {
      fromSession?: boolean;
    };

    const handleOpenQuickAdd = () => {
      setIsQuickAddOpen(true);
    };

    const handleOpenNewSession: EventListener = async (event: Event) => {
      const canCreate = await ensureCanCreateAction();
      if (!canCreate) return;
      const detail = (event as CustomEvent<OpenNewSessionDetail>).detail || {};
      const { editId, returnTo, preSelectedBankrollId } = detail;
      setNewSessionEditId(editId || null);
      setNewSessionReturnTo(returnTo || null);
      setNewSessionPreSelectedBankrollId(preSelectedBankrollId || null);
      setNewSessionMode(null);
      setIsNewSessionOpen(true);
    };

    const handleOpenAddBankroll: EventListener = async (event: Event) => {
      const canCreate = await ensureCanCreateAction();
      if (!canCreate) return;
      const detail = (event as CustomEvent<OpenAddBankrollDetail>).detail;
      if (detail?.fromSession) {
        addBankrollOpenedFromSessionRef.current = true;
      }
      setIsAddBankrollOpen(true);
    };

    window.addEventListener('openQuickAdd', handleOpenQuickAdd);
    window.addEventListener('openNewSession', handleOpenNewSession);
    window.addEventListener('openAddBankrollModal', handleOpenAddBankroll);

    return () => {
      window.removeEventListener('openQuickAdd', handleOpenQuickAdd);
      window.removeEventListener('openNewSession', handleOpenNewSession);
      window.removeEventListener('openAddBankrollModal', handleOpenAddBankroll);
    };
  }, [ensureCanCreateAction]);

  const handleQuickAddSelect = async (action: 'session' | 'transaction' | 'bankroll' | 'expense' | 'scanReceipt') => {
    const canCreate = await ensureCanCreateAction();
    if (!canCreate) {
      setIsQuickAddOpen(false);
      return;
    }
    setIsQuickAddOpen(false);
    switch (action) {
      case 'session':
        // Un seul bouton "Ajouter une session" → ouvrir le stepper avec tous les formats (screen 2)
        setIsNewSessionOpen(true);
        setNewSessionMode(null);
        break;
      case 'transaction':
        setIsSelectBankrollOpen(true);
        break;
      case 'expense':
        setIsAddExpenseOpen(true);
        break;
      case 'bankroll':
        setIsAddBankrollOpen(true);
        break;
      case 'scanReceipt':
        setIsScanReceiptOpen(true);
        break;
    }
  };

  const handleBankrollSelect = (bankrollId: string, currency: string) => {
    setSelectedBankrollId(bankrollId);
    setSelectedCurrency(currency);
    setIsSelectBankrollOpen(false);
    setIsAddTransactionOpen(true);
  };

  const handleTransactionSuccess = () => {
    setIsAddTransactionOpen(false);
    setSelectedBankrollId(null);
    setSelectedCurrency('EUR');
    // Optionnel: rafraîchir la page ou afficher une notification
    window.location.reload();
  };

  const handleTransactionClose = () => {
    setIsAddTransactionOpen(false);
    setSelectedBankrollId(null);
    setSelectedCurrency('EUR');
  };

  const handleExpenseSuccess = () => {
    setIsAddExpenseOpen(false);
    setScannedReceiptData(null);
    window.location.reload();
  };

  const handleScanComplete = (data: ParsedReceipt) => {
    setScannedReceiptData(data);
    setIsScanningReceipt(false);
    setIsScanReceiptOpen(false);
    setIsAddExpenseOpen(true);
  };

  const handleBankrollSuccess = () => {
    setIsAddBankrollOpen(false);
    if (addBankrollOpenedFromSessionRef.current) {
      addBankrollOpenedFromSessionRef.current = false;
      window.dispatchEvent(new CustomEvent('bankrollsUpdated'));
    } else {
      window.location.reload();
    }
  };

  const handleNewSessionClose = () => {
    setIsNewSessionOpen(false);
    setNewSessionMode(null);
    setNewSessionEditId(null);
    setNewSessionReturnTo(null);
    setNewSessionPreSelectedBankrollId(null);
    // Ne pas recharger lors d'une fermeture manuelle
  };

  const handleNewSessionSuccess = () => {
    setIsNewSessionOpen(false);
    setNewSessionMode(null);
    setNewSessionEditId(null);
    setNewSessionReturnTo(null);
    setNewSessionPreSelectedBankrollId(null);
    // Recharger seulement après création/modification réussie
    window.location.reload();
  };

  return (
    <>
      <QuickAddModal
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onSelect={handleQuickAddSelect}
        triggerScan={() => scanTriggerRef.current?.trigger()}
      />
      <SelectBankrollModal
        isOpen={isSelectBankrollOpen}
        onClose={() => setIsSelectBankrollOpen(false)}
        onSelect={handleBankrollSelect}
      />
      {selectedBankrollId && (
        <AddTransactionModal
          isOpen={isAddTransactionOpen}
          onClose={handleTransactionClose}
          onSuccess={handleTransactionSuccess}
          bankrollId={selectedBankrollId}
          currency={selectedCurrency}
        />
      )}
      <AddBankrollModal
        isOpen={isAddBankrollOpen}
        onClose={() => setIsAddBankrollOpen(false)}
        onSuccess={handleBankrollSuccess}
      />
      {(isQuickAddOpen || isScanReceiptOpen) && (
        <ReceiptScanButton
          triggerRef={scanTriggerRef}
          showButton={false}
          onScanned={handleScanComplete}
          onScanningChange={setIsScanningReceipt}
          onError={(msg) => {
            window.alert(msg);
            setIsScanningReceipt(false);
            setIsScanReceiptOpen(false);
          }}
        />
      )}
      {isScanningReceipt && (
        <div className="fixed inset-0 z-[103] flex flex-col items-center justify-center gap-4 bg-black/60 backdrop-blur-sm">
          <span className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
          <p className="text-sm font-medium text-white">Analyse du ticket...</p>
          <p className="text-xs text-white/80">Ouverture du formulaire</p>
        </div>
      )}
      <AddExpenseModal
        isOpen={isAddExpenseOpen}
        onClose={() => {
          setIsAddExpenseOpen(false);
          setScannedReceiptData(null);
        }}
        onSuccess={handleExpenseSuccess}
        prefillFromScan={scannedReceiptData}
        onPrefillConsumed={() => setScannedReceiptData(null)}
      />
      <NewSessionStepper
        isOpen={isNewSessionOpen}
        onClose={handleNewSessionClose}
        onSuccess={handleNewSessionSuccess}
        editSessionId={newSessionEditId}
        returnTo={newSessionReturnTo}
        preSelectedBankrollId={newSessionPreSelectedBankrollId}
        quickAddMode={newSessionMode ?? undefined}
      />
      <SubscriptionRequiredDialog
        open={isSubscriptionDialogOpen}
        onOpenChange={setIsSubscriptionDialogOpen}
        onSubscribe={() => openStripeResubscribeFlow(buildSubscribePath())}
      />
    </>
  );
};

